import type { MediaMenuAction } from '@lib/media/mediaActions';
import { inferProviderType } from '@lib/media/useMediaVerbs';
import { verbMenuIcon } from '@lib/verbs';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import DriveFileMoveOutlined from '@mui/icons-material/DriveFileMoveOutlined';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import ReplayIcon from '@mui/icons-material/Replay';
import { getProviderIcon } from '@pages/Library/components/MediaCard/components/providerIcons';
import { Media } from '@typings/Media';

/**
 * One glyph per media action, for every menu, drawer and bar that lists them.
 *
 * Lifted out of `MobileMediaMenuDrawer`, which owned the only copy while the desktop kebab
 * hardcoded its own icons inline — the drift `context-menu-actions.md` exists to prevent, and
 * the reason the moment modal's kebab reads from here rather than growing a third copy.
 *
 * The verbs read their glyph from `@lib/verbs` so a nav icon swap reaches them; the
 * housekeeping ones are local, because they have no counterpart anywhere else.
 */
export function getMediaActionIcon(action: MediaMenuAction, media: Media): React.ReactNode {
  switch (action) {
    case 'work-with-agent':
    case 'find-moments':
    case 'clip-podcast':
      return verbMenuIcon(action);
    // A chain link, not a copy glyph. What the row produces is a link; "copy" is just how you
    // get it, and it's the verb on several rows here. The copy glyph now belongs to
    // `copy-metadata` alone, where there is no better picture of the payload.
    case 'copy-link':
      return <LinkIcon fontSize="small" />;
    // A clock rather than a second link glyph: two identical icons in a vertical list read as
    // a duplicated row, and the difference between these two actions is entirely the time.
    case 'copy-link-at-time':
      return <AccessTimeIcon fontSize="small" />;
    case 'copy-metadata':
      return <ContentCopyIcon fontSize="small" />;
    case 'open-original':
      return getProviderIcon(inferProviderType(media));
    case 'rename':
      return <EditIcon fontSize="small" />;
    case 'move':
      return <DriveFileMoveOutlined fontSize="small" />;
    case 'download':
      return <DownloadIcon fontSize="small" />;
    case 'retry-processing':
      return <ReplayIcon fontSize="small" />;
    case 'delete':
      return <DeleteIcon fontSize="small" />;
  }
}
